import { AlertCircle } from 'lucide-react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DocumentFieldMapping } from '@/services/types/letters-types';

import { AdjusterContactField } from './field-controls/AdjusterContactField';
import { AdjusterNameField } from './field-controls/AdjusterNameField';
import { EnhancedSelect } from './shared/EnhancedSelect';
import type { BaseFieldProps, LoRField, LoRFormData } from './types';

interface LoRFieldRendererProps {
  field: LoRField;
  control: Control<LoRFormData>;
  required?: boolean;
  error?: string;
}

/**
 * Enhanced text input field with improved styling and validation
 */
const renderTextInputField = ({
  field,
  control,
  name,
  required,
  error,
}: BaseFieldProps) => {
  // Only allow multiline for very large free-form fields (e.g. client address or notes)
  const MULTILINE_MAPPINGS = new Set<DocumentFieldMapping>([
    // Add mappings here that genuinely need multiline inputs
    // DocumentFieldMapping.CLIENT_ADDRESS,
  ]);

  const isMultiline = MULTILINE_MAPPINGS.has(field.mapping);

  const isEmailField =
    field.mapping === DocumentFieldMapping.CLIENT_EMAIL_ADDRESS;
  const isPhoneField =
    field.mapping === DocumentFieldMapping.CLIENT_PHONE_NUMBER;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue=""
      rules={{
        required: required
          ? `${field.display_name || 'This field'} is required`
          : false,
        ...(isEmailField && {
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid email address',
          },
        }),
        ...(isPhoneField && {
          pattern: {
            value: /^[+]?[1-9][\d]{0,15}$/,
            message: 'Please enter a valid phone number',
          },
        }),
      }}
      render={({ field: ctrlField }) => (
        <div className="space-y-2">
          {isMultiline ? (
            <Textarea
              value={(ctrlField.value as string) || ''}
              onChange={ctrlField.onChange}
              onBlur={ctrlField.onBlur}
              name={ctrlField.name}
              placeholder={`Enter ${field.display_name?.toLowerCase() || 'text'}...`}
              className={`
                transition-all duration-200 resize-none
                ${
                  error
                    ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
                    : 'hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/20'
                }
              `}
              rows={2}
            />
          ) : (
            <Input
              value={(ctrlField.value as string) || ''}
              onChange={ctrlField.onChange}
              onBlur={ctrlField.onBlur}
              name={ctrlField.name}
              type={isEmailField ? 'email' : isPhoneField ? 'tel' : 'text'}
              placeholder={`Enter ${field.display_name?.toLowerCase() || 'text'}...`}
              className={`
                transition-all duration-200 h-10 text-sm
                ${
                  error
                    ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
                    : 'hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/20'
                }
              `}
            />
          )}

          {error && (
            <p className="text-xs text-red-600 font-medium flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3" />
              {error}
            </p>
          )}
        </div>
      )}
    />
  );
};

/**
 * Generic select field using shared EnhancedSelect component
 */
const renderGenericSelectField = ({
  field,
  control,
  name,
  required,
  error,
}: BaseFieldProps) => {
  const choices = Array.isArray(field.default_value) ? field.default_value : [];

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required
          ? `Please select or enter ${field.display_name?.toLowerCase() || 'a value'}`
          : false,
      }}
      render={({ field: ctrlField }) => (
        <EnhancedSelect
          value={Array.isArray(ctrlField.value) ? undefined : ctrlField.value}
          onChange={ctrlField.onChange}
          onBlur={ctrlField.onBlur}
          name={ctrlField.name}
          choices={choices}
          placeholder={`Choose ${field.display_name?.toLowerCase() || 'option'}...`}
          fieldDisplayName={field.display_name?.toLowerCase() || 'value'}
          error={error}
          emptyStateMessage="No options available from policy"
          emptyStateDescription="This field will be populated once you select a coverage policy with relevant data."
        />
      )}
    />
  );
};

/**
 * Main field renderer with enhanced routing and styling
 */
export const LoRFieldRenderer = ({
  field,
  control,
  required,
  error,
}: LoRFieldRendererProps) => {
  const fieldName = field.external_reference as keyof LoRFormData;
  const baseProps: BaseFieldProps = {
    field,
    control,
    name: fieldName,
    required,
    error,
  };

  // Route to specific field components for enhanced UX
  switch (field.mapping) {
    case DocumentFieldMapping.ADJUSTER_NAME:
      return (
        <AdjusterNameField
          {...baseProps}
          name={DocumentFieldMapping.ADJUSTER_NAME}
        />
      );

    case DocumentFieldMapping.ADJUSTER_CONTACT:
      return (
        <AdjusterContactField
          {...baseProps}
          name={DocumentFieldMapping.ADJUSTER_CONTACT}
        />
      );

    // Text input fields with enhanced validation and help text
    case DocumentFieldMapping.CLIENT_NAME:
    case DocumentFieldMapping.CLIENT_EMAIL_ADDRESS:
    case DocumentFieldMapping.CLIENT_PHONE_NUMBER:
    case DocumentFieldMapping.DATE_OF_LOSS:
      return renderTextInputField(baseProps);

    // Generic select fields using shared component
    case DocumentFieldMapping.CARRIER_NAME:
    case DocumentFieldMapping.CARRIER_ADDRESS_1:
    case DocumentFieldMapping.CARRIER_ADDRESS_2:
      return renderTextInputField(baseProps);

    case DocumentFieldMapping.POLICY_NUMBER:
    case DocumentFieldMapping.CLAIM_NUMBER:
    case DocumentFieldMapping.INSURED_NAME:
      return renderGenericSelectField(baseProps);

    default:
      // Fallback to text input with enhanced styling
      return renderTextInputField(baseProps);
  }
};
